import type { LightningBinaryChunk, LightningBinaryPayload } from "./LightningBinaryPayload";

export type LightningBinaryPoint = Readonly<{
  pointIndex: number;
  longitude: number;
  latitude: number;
  strikeTimeMs: number;
  currentKa: number | null;
  polarity: "+" | "-" | null;
  lightningType: 1 | 2 | null;
}>;

function findChunk(chunks: readonly LightningBinaryChunk[], pointIndex: number): LightningBinaryChunk | undefined {
  let lower = 0;
  let upper = chunks.length - 1;
  while (lower <= upper) {
    const middle = Math.floor((lower + upper) / 2);
    const chunk = chunks[middle];
    if (pointIndex < chunk.pointOffset) {
      upper = middle - 1;
    } else if (pointIndex >= chunk.pointOffset + chunk.pointCount) {
      lower = middle + 1;
    } else {
      return chunk;
    }
  }
  return undefined;
}

export function normalizedMercatorToLongitudeLatitude(x: number, y: number): [number, number] {
  const longitude = x * 360 - 180;
  const latitude = (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
  return [longitude, latitude];
}

/** Reads one selected entry without materializing or scanning the point arrays. */
export function decodeLightningBinaryPoint(
  payload: LightningBinaryPayload,
  pointIndex: number,
): LightningBinaryPoint | undefined {
  const { header } = payload;
  if (!Number.isInteger(pointIndex) || pointIndex < 0 || pointIndex >= header.totalPointCount) {
    return undefined;
  }
  const chunk = findChunk(header.chunks, pointIndex);
  if (!chunk) {
    return undefined;
  }

  const view = new DataView(payload.buffer);
  const positionOffset = header.positionsOffset + pointIndex * 8;
  const [longitude, latitude] = normalizedMercatorToLongitudeLatitude(
    view.getFloat32(positionOffset, true),
    view.getFloat32(positionOffset + 4, true),
  );

  if (header.wireVersion === 2) {
    const relativeMinutes = view.getUint16(header.timesOffset + pointIndex * 2, true);
    const wirePolarity = view.getUint8(header.polaritiesOffset + pointIndex);
    return {
      pointIndex,
      longitude,
      latitude,
      strikeTimeMs: (chunk.startEpoch + relativeMinutes * 60) * 1000,
      currentKa: null,
      polarity: wirePolarity === 0 ? "+" : wirePolarity === 1 ? "-" : null,
      lightningType: null,
    };
  }

  const relativeMinutes = view.getUint16(header.timesOffset + pointIndex * 2, true);
  const wirePolarity = view.getUint8(header.polaritiesOffset + pointIndex);
  const wireCurrent = view.getFloat32(header.currentsOffset + pointIndex * 4, true);
  const currentKa = Number.isFinite(wireCurrent) ? wireCurrent : null;
  const wireType = view.getUint8(header.typesOffset + pointIndex);
  return {
    pointIndex,
    longitude,
    latitude,
    strikeTimeMs: (chunk.startEpoch + relativeMinutes * 60) * 1000,
    currentKa,
    polarity: wirePolarity === 0 ? "+" : wirePolarity === 1 ? "-" : null,
    lightningType: wireType === 1 || wireType === 2 ? wireType : null,
  };
}
